/**
 * Task planned duration + reported progress (V1 Task Duration & Progress).
 *
 * PLANNED DURATION is one canonical value: integer minutes
 * (project_tasks.planned_duration_minutes / task_catalog.planned_duration_minutes).
 * It is planned WORKING time, not the calendar span from start to due date:
 * neither value is ever derived from, or overwrites, the other.
 *
 * Days are a display/input unit only. One working day = the organization's
 * Work Schedule hours per day (settings.work_schedule.minutes_per_day, default
 * 8 h; see helpers/tasks/workSchedule.js). Every day conversion takes that value
 * as `minutesPerDay`; when it is not known (null) durations are shown and
 * entered in hours only, so a day is never converted with a guessed length.
 *
 * PROGRESS is 0..100, reported by people from a time session. It is never
 * inferred from time worked and never moves start/due dates.
 */

export const DURATION_MAX_MINUTES = 525600;
export const DURATION_UNITS = Object.freeze([
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
]);

function trimNumber(value) {
  return String(Math.round(value * 100) / 100);
}

function validDay(minutesPerDay) {
  const mpd = Number(minutesPerDay);
  return Number.isFinite(mpd) && mpd > 0 ? mpd : null;
}

/** Whole or quarter working days (at least one) are shown in days. */
function showsAsDays(value, mpd) {
  return mpd != null && value >= mpd && value % (mpd / 4) === 0;
}

/**
 * With an 8 h day: 120 → "2h", 150 → "2h 30m", 480 → "1 day", 720 → "1.5 days".
 * With a 10 h day 600 → "1 day"; with minutesPerDay null 720 → "12h".
 */
export function formatDuration(minutes, minutesPerDay = null) {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return "";
  const mpd = validDay(minutesPerDay);
  if (showsAsDays(value, mpd)) {
    const days = value / mpd;
    return `${trimNumber(days)} ${days === 1 ? "day" : "days"}`;
  }
  const hours = Math.floor(value / 60);
  const rest = Math.round(value % 60);
  if (!hours) return `${rest}m`;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

/** Stored minutes → editor fields ({ amount: "1.5", unit: "days" }). */
export function durationToInput(minutes, minutesPerDay = null) {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return { amount: "", unit: "hours" };
  const mpd = validDay(minutesPerDay);
  if (showsAsDays(value, mpd)) {
    return { amount: trimNumber(value / mpd), unit: "days" };
  }
  return { amount: trimNumber(value / 60), unit: "hours" };
}

/**
 * Editor fields → { ok, minutes, error }. Empty amount = no planned duration
 * (minutes null). Rounds to whole minutes.
 */
export function parseDurationInput({ amount, unit = "hours" } = {}, minutesPerDay = null) {
  const text = String(amount ?? "").trim().replace(",", ".");
  if (!text) return { ok: true, minutes: null };
  if (!/^\d+(\.\d+)?$/.test(text)) return { ok: false, minutes: null, error: "Enter the duration as a number." };
  const number = Number(text);
  const mpd = validDay(minutesPerDay);
  if (unit === "days" && mpd == null) {
    return { ok: false, minutes: null, error: "The work schedule is not loaded, so enter the duration in hours." };
  }
  const factor = unit === "days" ? mpd : 60;
  const minutes = Math.round(number * factor);
  if (minutes <= 0) return { ok: false, minutes: null, error: "Duration must be more than zero." };
  if (minutes > DURATION_MAX_MINUTES) return { ok: false, minutes: null, error: "That duration is too long." };
  return { ok: true, minutes };
}

export function isValidDurationMinutes(value) {
  if (value == null) return true;
  return Number.isInteger(value) && value > 0 && value <= DURATION_MAX_MINUTES;
}

/** null/undefined → null; otherwise an integer 0..100, or NaN when invalid. */
export function normalizeProgress(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return NaN;
  const rounded = Math.round(number);
  if (rounded < 0 || rounded > 100) return NaN;
  return rounded;
}

/** Progress shown to people: "40%", or "" when never reported. Completed tasks are 100%. */
export function progressLabel(task) {
  const value = progressValue(task);
  return value == null ? "" : `${value}%`;
}

/** The task's displayed progress: the stored value; a completed task is 100. */
export function progressValue(task) {
  if (!task) return null;
  if (task.status === "completed") return 100;
  const value = normalizeProgress(task.progress_percent);
  return Number.isNaN(value) ? null : value;
}

/**
 * Canonical progress/status of a task, for pills (Calendar, lists):
 *   completed (status completed or 100)  → "Completed"
 *   1..99                                → "In Progress · N%"
 *   null / 0 (never reported, legacy)    → "Not Started"
 * Read from the task's stored status/progress only; never inferred.
 */
export const PROGRESS_STATE = Object.freeze({
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
});

export function taskProgressState(task) {
  const value = progressValue(task);
  if (value === 100) return { state: PROGRESS_STATE.COMPLETED, percent: 100, label: "Completed" };
  if (value == null || value === 0) return { state: PROGRESS_STATE.NOT_STARTED, percent: value, label: "Not Started" };
  return { state: PROGRESS_STATE.IN_PROGRESS, percent: value, label: `In Progress · ${value}%` };
}

/**
 * One recorded progress event → its HISTORICAL text, from the event's own
 * values only (never the task's current progress):
 *   "30% → 50%", "Started · 25%" (no previous value), "75% → 100% · Completed".
 */
export function describeProgressEvent(event) {
  const current = normalizeProgress(event?.progress_percent);
  if (current == null || Number.isNaN(current)) return null;
  const prevRaw = normalizeProgress(event?.previous_percent);
  const previous = Number.isNaN(prevRaw) ? null : prevRaw;
  const completed = current === 100;
  let text;
  if (previous == null) {
    if (completed) text = "Completed · 100%";
    else if (current === 0) text = "Not Started · 0%";
    else text = `Started · ${current}%`;
  } else {
    text = `${previous}% → ${current}%${completed ? " · Completed" : ""}`;
  }
  return { previous, current, completed, text };
}

/** Net worked time label for a session ("1h 20m"). Input in minutes. */
export function formatWorkedMinutes(minutes) {
  const value = Math.max(0, Math.round(Number(minutes) || 0));
  const hours = Math.floor(value / 60);
  const rest = value % 60;
  if (!hours) return `${rest}m`;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}
